/**
 * The reactive mirror of server state.
 *
 * One websocket per tab. The server sends a snapshot on connect and a frame per
 * changed slice thereafter, so every open tab renders the same transcript,
 * timeline, dashboard and orb state — the Convex behaviour, without Convex.
 */

import { reactive, readonly, ref } from "vue";
import { token } from "./api";
import { wsUrl } from "./backend";
import type { Snapshot } from "./types";

const empty: Snapshot = {
  profile: null,
  messages: [],
  memory: [],
  timeline: [],
  todos: { pending: [], done: [] },
  dashboard: [],
  objective: { text: "Standing by", state: "idle", updatedAt: 0 },
  pendingAction: null,
  pendingQuestion: null,
  voiceState: { orbState: "idle", sessionActive: false, updatedAt: 0 },
  connections: [],
  results: [],
  actions: [],
  knowledge: [],
  intake: [],
  inbox: { inbox: 0, unread: 0, sent: 0, tickets: 0, notifications: 0, ticketsTotal: 0, ticketEmails: 0, notificationsTotal: 0, flagged: 0, waiting: 0, triagePending: 0, attachments: 0, done: 0, everything: 0 },
  agents: [],
};

const state = reactive<Snapshot>(structuredClone(empty));

export const live = readonly(state) as Snapshot;
export const liveConnected = ref(false);

/**
 * Which thread this socket is watching. The server keeps it per-connection, so
 * two open tabs can sit in different conversations without fighting; this is
 * only our side's memory of the choice, replayed on reconnect.
 */
export const currentConversationId = ref<string | null>(null);

let socket: WebSocket | null = null;
/** The thread the open socket was asked for, to spot a change made while it connected. */
let openedWith: string | null = null;
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
let attempts = 0;
let closedByUs = false;

export function connectLive(): void {
  if (!token.value) return;
  if (socket && (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING)) {
    return;
  }

  closedByUs = false;
  openedWith = currentConversationId.value;
  const thread = openedWith ? `&conversationId=${encodeURIComponent(openedWith)}` : "";
  socket = new WebSocket(
    wsUrl(`/ws?token=${encodeURIComponent(token.value)}${thread}`)
  );

  socket.onopen = () => {
    attempts = 0;
    liveConnected.value = true;
    // The operator moved on while the socket was still connecting: send the
    // move now rather than showing the thread the socket was opened for.
    if (currentConversationId.value !== openedWith) {
      socket?.send(JSON.stringify({ type: "select", conversationId: currentConversationId.value ?? undefined }));
    }
  };

  socket.onmessage = (event) => {
    let frame: { type: string; slice?: keyof Snapshot; data?: unknown };
    try {
      frame = JSON.parse(event.data);
    } catch {
      return;
    }

    if (frame.type === "snapshot") {
      const resolved = (frame.data as { conversationId?: string }).conversationId;
      const wanted = currentConversationId.value;
      // A snapshot for a thread other than the one now wanted, when the want
      // changed after the socket was opened, is the connect-time snapshot
      // overtaken by a `select` already on its way. Rendering it would flash
      // the wrong transcript for a frame; the answer to the select follows.
      if (wanted && resolved && resolved !== wanted && wanted !== openedWith) return;
      Object.assign(state, frame.data as Snapshot);
      // The server resolves "no thread given" — or a thread it does not have —
      // to the most recent one; adopt its answer so a reconnect asks for the
      // same thread rather than drifting back to whatever is newest.
      if (resolved) currentConversationId.value = resolved;
      openedWith = currentConversationId.value;
    } else if (frame.type === "patch" && frame.slice) {
      applyPatch(frame.slice, frame.data);
    }
  };

  socket.onclose = (event) => {
    liveConnected.value = false;
    socket = null;
    // 4001 means the token was rejected; retrying would just loop.
    if (closedByUs || event.code === 4001) return;
    scheduleReconnect();
  };

  socket.onerror = () => socket?.close();
}

/**
 * Slices whose value is legitimately null — `profile` before the operator
 * fills one in, `pendingAction` whenever no write is waiting. For every other
 * slice a null is a malformed frame, and keeping the last good value beats
 * blanking the panel. This list matters most on the way DOWN: clearing the
 * confirmation card is a null patch, so dropping it would leave Confirm and
 * Cancel on screen for a change that has already run.
 */
const NULLABLE_SLICES = new Set<keyof Snapshot>(["profile", "pendingAction", "pendingQuestion"]);

function applyPatch(slice: keyof Snapshot, data: unknown): void {
  if (data === undefined) return;
  if (data === null && !NULLABLE_SLICES.has(slice)) return;
  (state as Record<string, unknown>)[slice] = data;
}

function scheduleReconnect(): void {
  if (reconnectTimer) clearTimeout(reconnectTimer);
  attempts += 1;
  const delay = Math.min(1000 * 2 ** (attempts - 1), 20_000);
  reconnectTimer = setTimeout(connectLive, delay);
}

export function disconnectLive(): void {
  closedByUs = true;
  if (reconnectTimer) clearTimeout(reconnectTimer);
  socket?.close();
  socket = null;
  liveConnected.value = false;
  Object.assign(state, structuredClone(empty));
}

/** Reads a dashboard card by key, or null when Jarvis hasn't filled it yet. */
/**
 * Move this socket to another thread. The server replies with a full snapshot
 * for it, so the transcript swaps in one frame rather than being rebuilt by
 * the client.
 */
export function selectConversation(id: string | null): void {
  if (id !== null && currentConversationId.value === id) return;
  currentConversationId.value = id;
  if (socket?.readyState === WebSocket.OPEN) {
    // `null` asks for the server's default — the most recent thread — which
    // is what the home screen peeks at.
    socket.send(JSON.stringify({ type: "select", conversationId: id ?? undefined }));
  } else {
    connectLive();
  }
}

export function card<T = Record<string, unknown>>(key: string): T | null {
  const found = state.dashboard.find((c) => c.key === key);
  return (found?.data as T) ?? null;
}
